import {
  ApplicationConfig,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  NavigationError,
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';

import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';

import { APP_BASE_HREF } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../environments/environment';
// eslint-disable-next-line local-rules/no-app-env-config-injection
import { APP_ENV_CONFIG } from '../common/config/app-config';
import { authenticationInterceptor } from '../common/interceptors/authentication.interceptor';
import { captchaTokenInterceptor } from '../common/services/captcha/captcha-token.interceptor';
import { loadingInterceptor } from '../common/interceptors/loading.interceptor';
import { retryInterceptor } from '../common/interceptors/retry.interceptor';
import { LogFactory } from '../common/services/logger/log-factory';
import { PlatformService } from '../common/services/platform/platform.service';
import { routes } from './app.routes';
import { GlobalErrorHandlerService } from './core/error-handler/global-error-handler.service';
import { httpErrorInterceptor } from './core/interceptor/http-error-interceptor/http-error.interceptor';
import { ConfirmationService } from './core/service/confirmation.service';
import { MessageService } from './core/service/message.service';
import { MENU_PAGE_BACK_TARGET } from '../common/services/navigation/menu-page-back-target';
import { TabsShellService } from './features/sequal/tabs/tabs-shell.service';
import { BODY_METRIC_WRITER } from '../common/services/body-profile/body-metric-writer';
import { BodyProfileService } from '../common/services/body-profile/body-profile.service';
import { SESSION_RESET } from '../common/services/session-reset/session-reset';
import { DbService } from './features/sequal/services/db.service';
import { MediaCryptoService } from './features/sequal/services/media-crypto.service';
import { MediaSourceService } from './features/sequal/services/media-source.service';
import { MEDIA_AT_REST_CRYPTO } from './core/service/media/media-at-rest-crypto';
import { WorkoutService } from './features/sequal/services/workout.service';
import { PlanReminderService } from './features/sequal/services/plan-reminder.service';
import { PlaybackSourceService } from './features/sequal/services/playback-source.service';
import { VariantPreferenceService } from './features/sequal/services/variant-preference.service';
import { AudioPreferenceService } from './features/sequal/services/audio-preference.service';
import { ProfileService } from './core/pages/settings/profile/profile.service';
import { provideNotificationSource } from './core/notifications/services/notification-source';
import { SupportNotificationService } from './features/support/services/support-notification.service';
import { MessagingSocketService } from './features/messaging/services/messaging-socket.service';
import { APP_HTTP_HOOKS } from '../common/interceptors/app-http-hooks';
import { APP_REFRESH } from './core/visibility/app-refresh';
import { VersionGateService } from './core/version-gate/version-gate.service';
import { AppNotificationService } from './core/notifications/services/app-notification.service';
import { SubscriptionService } from './core/payment/services/subscription.service';
import { PurchaseAvailabilityService } from './core/payment/services/purchase-availability.service';
import { PublishingAvailabilityService } from './features/sequal/services/publishing-availability.service';
import { SupportAvailabilityService } from './features/support/services/support-availability.service';
import { SUPPORT_AVAILABILITY } from './core/support/support-link.service';
import { PageTitleStrategy } from '../common/services/page-title/page-title.strategy';

export function baseHrefFactory(): string {
  return Capacitor.isNativePlatform() ? './' : '/';
}

export const baseHrefProvider = {
  provide: APP_BASE_HREF,
  useFactory: baseHrefFactory,
  deps: [] as never[],
};

// Enable prod mode if ENVIRONMENT is 'Production'
if (environment.app.production) {
  enableProdMode();
}

export const BOOTSTRAP_CONFIG: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideZonelessChangeDetection(),
    LogFactory,
    PlatformService,
    baseHrefProvider,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    // Tab title from env config (`app.name` — `app.tagline`), not index.html.
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    provideIonicAngular(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withEnabledBlockingInitialNavigation(),
      // A failed lazy-chunk import (e.g. PWA tab left open across a deploy →
      // stale hashed chunk 404s) rejects the navigation Promise instead of
      // reaching Angular's ErrorHandler. Forward it so GlobalErrorHandler can
      // detect the stale build and surface the reload prompt.
      withNavigationErrorHandler((error: NavigationError) =>
        inject(GlobalErrorHandlerService).handleError(error.error)
      )
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        authenticationInterceptor,
        // Carries the security-check token onto the credential request.
        captchaTokenInterceptor,
        httpErrorInterceptor,
        // Innermost (closest to the backend): transparently retries cold-start
        // failures (Railway sleep) before the error interceptor above shows a
        // toast, so retries run while the loading spinner is still up.
        retryInterceptor,
      ])
    ),
    {
      provide: APP_ENV_CONFIG,
      useValue: environment, // Use the UI app's concrete service
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    MessageService,
    ConfirmationService,
    // Binds the common weight-history seam to the feature DbService so the core
    // Profile body-details form can log weight without a core → features import.
    { provide: BODY_METRIC_WRITER, useExisting: DbService },
    // Same shape: the media store lives in core and must not reach into
    // features for the at-rest cipher it uses on the web driver.
    { provide: MEDIA_AT_REST_CRYPTO, useExisting: MediaCryptoService },
    // Services caching state that belongs to the signed-in user. cleanSession()
    // calls resetOnLogout() on each, so no account inherits the previous one's
    // data. Add every new user-scoped cache here; if it also persists, add its
    // key to UserScopedStorageKey so the sweep covers a never-instantiated one.
    { provide: SESSION_RESET, useExisting: BodyProfileService, multi: true },
    { provide: SESSION_RESET, useExisting: VariantPreferenceService, multi: true },
    { provide: SESSION_RESET, useExisting: AudioPreferenceService, multi: true },
    { provide: SESSION_RESET, useExisting: ProfileService, multi: true },
    { provide: SESSION_RESET, useExisting: SupportNotificationService, multi: true },
    // Cached streaming URLs are presigned for the account that asked; the next
    // one on this device must not be handed them.
    { provide: SESSION_RESET, useExisting: MediaSourceService, multi: true },
    // Not a cache: a running workout holds a timer, audio, TTS and a keep-awake
    // lock that otherwise keep playing over the login screen after a sign-out.
    { provide: SESSION_RESET, useExisting: WorkoutService, multi: true },
    // Not a cache either: an open realtime socket was authorised with the
    // previous account's token, and the next account's screens would reuse it.
    { provide: SESSION_RESET, useExisting: MessagingSocketService, multi: true },
    // Scheduled on the device, not cached: the previous account's plan reminders
    // name its programs and would keep arriving for whoever signs in next.
    { provide: SESSION_RESET, useExisting: PlanReminderService, multi: true },
    // Audio fetched for the workout (cache files, in-memory tracks) came from
    // the previous account's media.
    { provide: SESSION_RESET, useExisting: PlaybackSourceService, multi: true },
    // Server state that goes stale while the app is away (ADR-0264). No service
    // refreshes itself on resume: AppRefreshCoordinator marks every entry stale
    // and fetches only the EAGER ones that have aged; a LAZY one waits for the
    // screen that reads it to pull it. The bar for EAGER is "the user sees it
    // the moment they arrive" — each service's own doc says why it qualifies.
    { provide: APP_REFRESH, useExisting: VersionGateService, multi: true },
    { provide: APP_REFRESH, useExisting: AppNotificationService, multi: true },
    { provide: APP_REFRESH, useExisting: SubscriptionService, multi: true },
    { provide: APP_REFRESH, useExisting: PurchaseAvailabilityService, multi: true },
    { provide: APP_REFRESH, useExisting: PublishingAvailabilityService, multi: true },
    { provide: APP_REFRESH, useExisting: SupportAvailabilityService, multi: true },
    // Lets core/ screens ("Problem with this purchase? Contact us") ask whether
    // Help can open without importing the support feature.
    { provide: SUPPORT_AVAILABILITY, useExisting: SupportAvailabilityService },
    // App-only request behaviour for the shared auth interceptor: the platform
    // + build headers and the blocking screen on a 426. Admin provides none.
    { provide: APP_HTTP_HOOKS, useExisting: VersionGateService },
    // Learn the platform + build BEFORE the first request goes out. The policy
    // check still runs from AppComponent through the refresh coordinator (it
    // may put a blocking alert on screen, which bootstrap must not wait for),
    // but the two headers the gate stamps have to be ready sooner than that:
    // boot is when the app makes its first calls, and an unstamped request is
    // one the API can neither gate nor branch on (ADR-0281). No network,
    // cannot reject.
    provideAppInitializer(() => inject(VersionGateService).loadBuild()),
    // Binds the common menu-page Back seam to the tabs shell, so the shared
    // AppHeaderComponent can send a menu-reached page's Back button to the tab
    // the user came from without importing this app's tab shell.
    { provide: MENU_PAGE_BACK_TARGET, useExisting: TabsShellService },
    // Feeds the header's notification bell. Every source registered here is
    // counted by AppNotificationService; the bell itself imports no feature, so
    // a new notification is one more line here plus a member of
    // APP_NOTIFICATIONS — never a change to the header or to any screen.
    provideNotificationSource(SupportNotificationService),
  ],
};
